import { RoutalPalette, RoutalSelect } from '@nx-smartmonkey/ui';
import React, { useEffect, useState } from 'react';

import { useIntl } from 'react-intl';
import { ContextualMenu } from '../../../components/menus/ContextualMenu';
import { ConfirmDeleteDialog } from '../../../dialogs/ConfirmDeleteDialog';
import { DuplicatePlanDialog } from '../../../dialogs/DuplicatePlanDialog';
import { PlanStatusDialog } from '../../../dialogs/PlanStatusDialog';
import { PlanFlat } from '../../../domain/plan/PlanFlat';
import { PlanStatus } from '../../../domain/plan/PlanStatus';
import { useDeleteFlatPlans } from '../../../hooks/plan/useDeleteFlatPlans';
import { useDuplicatePlan } from '../../../hooks/plan/useDuplicatePlan';
import { useRetrieveActiveProject } from '../../../hooks/project/useRetrieveActiveProject';
import Text from '../../../react-components/Text';
import { useManagePlanQuery } from './hooks/useManagePlanQuery';

type PlanContextAnchor = {
  screenPosition: {
    top: number;
    left: number;
  };
};

type PlanActionsMenuType = `contextual` | `popover`;

type PlanActionsMenuProps = {
  plans: Array<PlanFlat>;
  type?: PlanActionsMenuType;
  showOpenPlan?: boolean;
  showCopyPlan?: boolean;
  showDeletePlan?: boolean;
  showUpdatePlanStatus?: boolean;
  contextAnchor?: PlanContextAnchor;
  popoverProps?: {
    hideArrow?: boolean;
    style?: React.CSSProperties;
    containerStyle?: React.CSSProperties;
    content?: React.ReactElement;
  };
  onCloseAnyDialog?: () => void;
  onDeletePlan?: (deletedPlans: Array<PlanFlat>) => void;
  onUpdatePlanStatus?: () => void;
  onDuplicatePlan?: () => void;
  setContextAnchor?: (context?: PlanContextAnchor) => void;
};

export const PlanActionsMenu = ({
  plans,
  type = `contextual` as PlanActionsMenuType,
  contextAnchor = {
    screenPosition: {
      top: 0,
      left: 0,
    },
  },
  showOpenPlan = true,
  showCopyPlan = true,
  showDeletePlan = true,
  showUpdatePlanStatus = true,
  popoverProps,
  onCloseAnyDialog,
  onDeletePlan,
  onUpdatePlanStatus,
  onDuplicatePlan,
  setContextAnchor = () => {},
}: PlanActionsMenuProps) => {
  const intl = useIntl();
  const { navigateToPlan } = useManagePlanQuery();
  const { data: activeProject } = useRetrieveActiveProject();
  const [dialog, setDialog] = useState<`delete` | `update_status` | `duplicate` | undefined>(undefined);

  const {
    success: duplicatePlanSuccess,
    data: createdDuplicates,
    isLoading: duplicatePlanLoading,
    run: duplicatePlan,
  } = useDuplicatePlan();
  const { success: deletePlansSuccess, run: deletePlans, isLoading: deletePlansLoading } = useDeleteFlatPlans();

  useEffect(() => {
    if (deletePlansSuccess) {
      setDialog(undefined);

      if (onCloseAnyDialog) {
        onCloseAnyDialog();
      }

      if (setContextAnchor) {
        setContextAnchor();
      }

      if (onDeletePlan) {
        onDeletePlan(plans);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deletePlansSuccess]);

  useEffect(() => {
    if (duplicatePlanSuccess) {
      setDialog(undefined);

      if (onCloseAnyDialog) {
        onCloseAnyDialog();
      }

      if (setContextAnchor) {
        setContextAnchor();
      }

      if (onDuplicatePlan) {
        onDuplicatePlan();
      }

      if (activeProject && (createdDuplicates ?? []).length === 1 && createdDuplicates![0] !== null) {
        navigateToPlan({ planId: createdDuplicates![0].id, invalidateQueries: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [duplicatePlanSuccess, createdDuplicates]);

  return (
    <>
      {type === `popover` && (
        <RoutalSelect
          id="plan-actions-menu-select"
          hideArrow={popoverProps?.hideArrow}
          style={{
            background: `transparent`,
            ...(popoverProps?.style ?? {}),
          }}
          containerStyle={popoverProps?.containerStyle}
          popoverProps={{
            options: [
              ...(!showOpenPlan || plans.length !== 1
                ? []
                : [
                    {
                      key: `open_plan`,
                      width: 240,
                      onClick: () => {
                        navigateToPlan({ planId: plans[0].id });
                      },
                      content: <Text intlId="planner.plan_actions.open" style={{ cursor: `pointer` }} />,
                    },
                  ]),

              ...(!showCopyPlan || plans.length !== 1
                ? []
                : [
                    {
                      key: `duplicate_plan`,
                      width: 240,
                      onClick: () => {
                        setDialog(`duplicate`);
                      },
                      content: <Text intlId="planner.plan_actions.duplicate_plan" style={{ cursor: `pointer` }} />,
                    },
                  ]),
              ...(!showUpdatePlanStatus
                ? []
                : [
                    {
                      key: `change_plan_status`,
                      width: 240,
                      onClick: () => {
                        setDialog(`update_status`);
                      },
                      content: <Text intlId="planner.plan_actions.change_status" style={{ cursor: `pointer` }} />,
                    },
                  ]),
              ...(showDeletePlan
                ? [
                    null,
                    {
                      key: `delete_plan`,
                      width: 240,
                      onClick: () => {
                        setDialog(`delete`);
                      },
                      content: (
                        <Text
                          intlId="planner.plan_actions.delete"
                          style={{ color: RoutalPalette.error.medium, cursor: `pointer` }}
                        />
                      ),
                    },
                  ]
                : []),
            ],
          }}
        >
          {popoverProps?.content}
        </RoutalSelect>
      )}
      {type === `contextual` && (
        <>
          {contextAnchor ? (
            <ContextualMenu
              styledMenuItemStyles={{
                width: `240px`,
              }}
              anchorElementPosition={contextAnchor.screenPosition}
              onClose={() => {
                if (setContextAnchor) {
                  setContextAnchor();
                }
              }}
              options={[
                ...(!showOpenPlan || plans.length !== 1
                  ? []
                  : [
                      {
                        key: `open_plan`,
                        text: <Text intlId="planner.plan_actions.open" style={{ cursor: `pointer` }} />,
                        onClick: (event: any) => {
                          event.stopPropagation();
                          navigateToPlan({ planId: plans[0].id });

                          if (setContextAnchor) {
                            setContextAnchor();
                          }
                        },
                      },
                    ]),
                ...(!showCopyPlan || plans.length !== 1
                  ? []
                  : [
                      {
                        key: `duplicate_plan`,
                        text: <Text intlId="planner.plan_actions.duplicate_plan" style={{ cursor: `pointer` }} />,
                        onClick: (event: any) => {
                          event.stopPropagation();
                          setDialog(`duplicate`);
                        },
                      },
                    ]),
                ...(!showUpdatePlanStatus
                  ? []
                  : [
                      {
                        key: `change_plan_status`,
                        text: <Text intlId="planner.plan_actions.change_status" style={{ cursor: `pointer` }} />,
                        onClick: (event: any) => {
                          event.stopPropagation();
                          setDialog(`update_status`);
                        },
                      },
                    ]),
                ...(showDeletePlan
                  ? [
                      null,
                      {
                        key: `delete_plan`,
                        text: (
                          <Text
                            intlId="planner.plan_actions.delete"
                            style={{ color: RoutalPalette.error.medium, cursor: `pointer` }}
                          />
                        ),
                        onClick: (event: any) => {
                          event.stopPropagation();
                          setDialog(`delete`);
                        },
                      },
                    ]
                  : []),
              ]}
            />
          ) : null}
        </>
      )}

      {dialog === `duplicate` && (
        <DuplicatePlanDialog
          isLoading={duplicatePlanLoading}
          handleDuplicatePlan={(duplicates: { label: string; status: PlanStatus; execution_date: Date }[]) => {
            duplicatePlan({ sourcePlan: plans[0], duplicates });
          }}
          duplicateDialogOpen={dialog === `duplicate`}
          handleOpenDuplicateDialog={() => {
            setDialog(undefined);

            if (onCloseAnyDialog) {
              onCloseAnyDialog();
            }
          }}
        />
      )}

      <ConfirmDeleteDialog
        typeData="plans"
        open={dialog === `delete` || deletePlansLoading}
        onCloseDialog={() => {
          setDialog(undefined);

          if (onCloseAnyDialog) {
            onCloseAnyDialog();
          }

          if (setContextAnchor) {
            setContextAnchor();
          }
        }}
        data={plans}
        loading={deletePlansLoading}
        confirmInputValue={intl.formatMessage({ id: `confirm` })}
        handleDelete={() => {
          deletePlans({ flatPlans: plans });
        }}
      />

      {dialog === `update_status` && (
        <PlanStatusDialog
          plans={plans}
          dialogOpen={dialog === `update_status`}
          onCloseDialog={() => {
            setDialog(undefined);

            if (onCloseAnyDialog) {
              onCloseAnyDialog();
            }

            if (setContextAnchor) {
              setContextAnchor();
            }
          }}
          onUpdatePlanStatus={() => {
            if (onUpdatePlanStatus) {
              onUpdatePlanStatus();
            }
          }}
        />
      )}
    </>
  );
};

PlanActionsMenu.defaultProps = {
  type: `contextual` as PlanActionsMenuType,
  activeProject: undefined,
  showOpenPlan: true,
  showCopyPlan: true,
  showDeletePlan: true,
  showUpdatePlanStatus: true,
  contextAnchor: {
    screenPosition: {
      top: 0,
      left: 0,
    },
  },
  setContextAnchor: () => {},
};
